import { useEffect, useMemo, useState } from 'react';
import { Leaderboard } from '../components/Leaderboard';
import { BrandStats } from '../components/BrandStats';
import { sampleLeaderboard, sampleBrandStats } from '../lib/demoData';
import { createHost } from '../lib/host';
import { applyTheme, type Theme } from '../lib/theme';

/**
 * Local preview surface. Mounts both widgets with realistic sample data and
 * a fake StandaloneHost so the visual design can be reviewed end-to-end
 * (interactivity included — clicking a brand logs the simulated tool call).
 */
export function Demo() {
  const [theme, setTheme] = useState<Theme>('dark');

  // Standalone host — same surface as production, fakes callTool.
  const leaderboardHost = useMemo(() => createHost(sampleLeaderboard), []);
  const brandStatsHost = useMemo(() => createHost(sampleBrandStats), []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <div className="min-h-screen bg-background text-primary">
      <header className="max-w-5xl mx-auto px-6 pt-10 pb-6 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-accent font-medium mb-1">42a mcp widgets</div>
          <h1 className="text-2xl font-semibold text-primary">Widget gallery</h1>
          <p className="text-sm text-secondary mt-1 max-w-2xl">
            Same components ChatGPT and Claude render inline at tool-call time. This page mounts them against a
            standalone bridge so the look + feel + interactivity can be reviewed without an MCP server.
          </p>
        </div>

        <div className="flex items-center gap-2" role="group" aria-label="Theme preview toggle">
          <ThemeButton current={theme} value="light" onClick={setTheme}>
            Light
          </ThemeButton>
          <ThemeButton current={theme} value="dark" onClick={setTheme}>
            Dark
          </ThemeButton>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-16 space-y-10">
        <section>
          <SectionHeader title="Leaderboard" tool="list_top_brands_in_project" />
          <Leaderboard data={sampleLeaderboard} host={leaderboardHost} />
        </section>

        <section>
          <SectionHeader title="Brand stats" tool="get_brand_stats" />
          <BrandStats data={sampleBrandStats} host={brandStatsHost} />
        </section>

        <section className="border-t border-border pt-6 text-xs text-muted leading-relaxed">
          <p>
            Clicking a brand row in the leaderboard would normally call{' '}
            <code className="font-mono text-secondary">get_brand_stats</code> via the host bridge. Open the console to
            see the standalone-host stub fire.
          </p>
        </section>
      </main>
    </div>
  );
}

function ThemeButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Theme;
  value: Theme;
  onClick: (t: Theme) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onClick(value)}
      className={`text-xs px-3 py-1.5 rounded border transition focus-visible:ring-2 focus-visible:ring-accent outline-none ${
        active ? 'bg-accent text-white border-accent' : 'bg-surface text-secondary border-border hover:bg-surface-hover'
      }`}
    >
      {children}
    </button>
  );
}

function SectionHeader({ title, tool }: { title: string; tool: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 mb-3">
      <div>
        <h2 className="text-sm font-semibold text-primary">{title}</h2>
        <code className="font-mono text-[11px] text-muted">{tool}</code>
      </div>
    </div>
  );
}
